import { useEffect, useState } from "react";

interface Participant {
  id: number;
  name: string;
  avatar: string;
}

interface YarningRingProps {
  participants: Participant[];
  initialSpeakerId?: number;
  educatorMode?: boolean;
}

/**
 * Displays a ring of avatars with the current speaker highlighted as per the
 * “Yarning-Circle” spec.  Token-passing logic is mocked locally.  Realtime
 * media-controls (mic un-mute etc.) will be wired later.
 */
export default function YarningRing({
  participants,
  initialSpeakerId = participants[0]?.id,
  educatorMode = false,
}: YarningRingProps) {
  const [speakerIdx, setSpeakerIdx] = useState(() =>
    participants.findIndex((p) => p.id === initialSpeakerId)
  );

  // keyboard shortcut → to pass token
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        passToken();
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  });

  const passToken = () => {
    setSpeakerIdx((i) => (i + 1) % participants.length);
  };

  const size = 240;
  const avatarSize = 56;

  const centre = size / 2;
  const radius = (size - avatarSize) / 2;

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative"
        style={{ width: size, height: size }}
      >
        {participants.map((p, idx) => {
          const angle = (idx / participants.length) * 2 * Math.PI - Math.PI / 2;
          const x = centre + radius * Math.cos(angle) - avatarSize / 2;
          const y = centre + radius * Math.sin(angle) - avatarSize / 2;

          const isSpeaker = idx === speakerIdx;
          const isYou = idx === 0; // demo – first participant is “you”

          return (
            <div
              key={p.id}
              className="absolute flex flex-col items-center"
              style={{ left: x, top: y, width: avatarSize, height: avatarSize }}
            >
              <div
                className={
                  "relative rounded-full ring-3 ring-white dark:ring-gray-700" +
                  (isSpeaker ? " animate-pulse ring-[color:var(--c-info,#2563EB)]" : "")
                }
              >
                <img
                  src={p.avatar}
                  alt={p.name}
                  className="h-full w-full rounded-full object-cover"
                />
                {isYou && (
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded bg-primary px-1.5 text-xs font-medium text-white shadow">
                    You
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {educatorMode && (
        <button
          onClick={passToken}
          className="rounded-md bg-primary px-3 py-1.5 text-white hover:bg-primary-dark focus:outline-hidden focus:ring-3 focus:ring-primary"
        >
          Pass Token →
        </button>
      )}

      {/* aria-live list for screen-readers */}
      <div className="sr-only" aria-live="polite">
        Now sharing: {participants[speakerIdx]?.name}
      </div>
    </div>
  );
}
